import { useState, useEffect } from "react";
import { OfflineItemService } from "../../../../../services/api/OfflineOrder/offline.service";
import { OfflineOrderDetailsProps } from "./types";
import ReturnMain from "./ReturnOrder/ReturnMain";
import { ChevronLeftCircle, } from "lucide-react";
import Bill from "../../../../../shared/Bill";

export const OfflineOrderDetails: React.FC<OfflineOrderDetailsProps> = ({
  data,
  setShowOfflineOrderDetails,
}) => {
  console.log("Offline order data", data)
  const [isReturnable, setIsReturnable] = useState<boolean | null>(null);
  const [isCheckingReturnStatus, setIsCheckingReturnStatus] = useState(false);
  const [showReturnComponent, setShowReturnComponent] = useState(false);
  const [showBillPreview, setShowBillPreview] = useState(false);
  const [returnStatusMessage, setReturnStatusMessage] = useState<string | null>(
    null
  );

  // Automatically check return status when component mounts
  useEffect(() => {
    const checkReturnStatus = async () => {
      try {
        setIsCheckingReturnStatus(true);
        setReturnStatusMessage("Checking return status...");

        const response = await OfflineItemService.checkReturnItemStatus(
          data.orderId.toString()
        );
        const result = await response.json();

        if (result.success) {
          setIsReturnable(result.status);
          if (!result.status) {
            setReturnStatusMessage("Return order is completed");
          } else {
            setReturnStatusMessage(null);
          }
        } else {
          setIsReturnable(false);
          setReturnStatusMessage(result.message || "Return not allowed");
        }
      } catch (error) {
        console.error("Error checking return status:", error);
        setIsReturnable(false);
        setReturnStatusMessage("Error checking return status");
      } finally {
        setIsCheckingReturnStatus(false);
      }
    };

    checkReturnStatus();
  }, [data.orderId]);

  const handleReturn = () => {
    if (isReturnable) {
      setShowReturnComponent(true);
    }
  };

  const handleView = () => {
    setShowBillPreview(true);
  };

  const handleCloseBillPreview = () => {
    setShowBillPreview(false);
  };

  const handleBackToDetails = () => {
    setShowReturnComponent(false);
  };

  if (showReturnComponent) {
    return <ReturnMain orderDetails={data} onBack={handleBackToDetails} />;
  }

  const calculatePrice = (mrp: number, discount: number) => {
    const price = mrp - (mrp * discount / 100);
    return price;
  };

  return (
    <>

      {showBillPreview ? (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto relative"
            style={{
              scrollbarWidth: "thin"
            }}
          >
            <Bill orderId={data.orderId} orderType="OFFLINE" onClose={handleCloseBillPreview} />
          </div>
        </div>
      ) : null}


      <button
        onClick={() => setShowOfflineOrderDetails(false)}
        className="flex items-center gap-2 mb-4 text-blac"
      >
        <ChevronLeftCircle size={20} />
        Back to Orders
      </button>
      <div className="w-full p-6 bg-white">
        {/* Header Section */}
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <div className="grid grid-cols-7 gap-4 text-sm">
            <div>
              <span className="font-medium text-gray-600">Patient Name</span>
              <p className="text-gray-900 mt-2">{data.patientName}</p>
            </div>
            <div>
              <span className="font-medium text-gray-600">Mobile Number</span>
              <p className="text-gray-900 mt-2">{data.patientMobileNumber}</p>
            </div>
            <div>
              <span className="font-medium text-gray-600">Invoice number</span>
              <p className="text-gray-900 mt-2">{data.invoiceNumber}</p>
            </div>
            <div>
              <span className="font-medium text-gray-600">Doctor Name</span>
              <p className="text-gray-900 mt-2">{data.doctorName}</p>
            </div>
            <div>
              <span className="font-medium text-gray-600">Amount</span>
              <p className="text-gray-900 mt-2">₹{data.totalAmount}</p>
            </div>
            <div>
              <span className="font-medium text-gray-600">No.of Product</span>
              <p className="text-gray-900 mt-2">{data.totalItems}</p>
            </div>
            <div>
              <span className="font-medium text-gray-600">Order Date</span>
              <p className="text-gray-900 mt-2">
                {data?.orderDate && !isNaN(new Date(data.orderDate).getTime())
                  ? new Date(data.orderDate).toLocaleDateString()
                  : "N/A"}
              </p>
            </div>

          </div>
        </div>

        {/* Product Details Section */}
        <div className="bg-white rounded-lg overflow-hidden">
          <div className=" text-green-600 p-3">
            <h2 className="text-lg font-semibold">PRODUCT DETAILS</h2>
          </div>

          {/* Table Header */}
          <div className="bg-cyan-50 border-b border-gray-200">
            <div className="grid grid-cols-11 gap-2 p-3 text-sm font-medium text-gray-600">
              <div>Product Name</div>
              <div className="text-center">Strips</div>
              <div className="text-center">Tablets</div>
              <div className="text-center">Pack</div>
              <div className="text-center">MRP </div>
              <div className="text-center">MRP Per unit </div>
              <div className="text-center">Discount</div>
              <div className="text-center">Price per unit</div>
              <div className="text-center">CGST</div>
              <div className="text-center">SGST</div>
              <div className="text-center">Total Price</div>
            </div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-gray-200">
            {data.productDetails.map((product, index) => (
              <div
                key={index}
                className="grid grid-cols-11 gap-2 p-3 text-sm hover:bg-gray-50"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                    <img
                      src={product.productImage}
                      alt={product.productName}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {product.productName}
                    </p>
                  </div>
                </div>
                <div className="text-center text-gray-900">
                  {Math.floor(product.productsItemsCount / (product.tabletsPerStripe ?? 1))} Strips
                </div>
                <div className="text-center text-gray-900">
                  {Math.floor(product.productsItemsCount % (product.tabletsPerStripe ?? 1))} Tablets
                </div>
                <div className="text-center text-gray-900">
                  {product.tabletsPerStripe ?? 1}
                </div>
                
                <div className="text-center text-gray-900">
                  ₹{Number(product?.productMrp ?? 0).toFixed(2)}
                </div>
                <div className="text-center text-gray-900">
                  ₹{(Number(product?.productMrp ?? 0)/Number(product.tabletsPerStripe || 1)).toFixed(2)}
                </div>
                <div className="text-center text-gray-900">{product.sellingDiscount}%</div>
                <div className="text-center text-gray-900">
                  ₹{((product?.totalPriceOfProduct ?? 0) / Number(product.productsItemsCount ?? 1)).toFixed(2)}
                </div>
                <div className="text-center text-gray-900">{product.cgst}%</div>
                <div className="text-center text-gray-900">{product.sgst}%</div>
                <div className="text-center text-gray-900">
                  ₹{product?.totalPriceOfProduct.toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center justify-end space-x-3 p-4">
          {/* Show status message or return button */}
          {isCheckingReturnStatus ? (
            <div className="px-6 py-2 rounded-lg font-medium text-gray-600">
              Checking return status...
            </div>
          ) : returnStatusMessage ? (
            <div className="px-6 py-2 rounded-lg font-medium text-red-500">
              {returnStatusMessage}
            </div>
          ) : (
            <button
              onClick={handleReturn}
              className="px-6 py-2 border-2 border-red-400 text-red-500 rounded-lg font-medium hover:bg-red-50 hover:border-red-500 hover:text-red-600 transition-all duration-200"
            >
              Return
            </button>
          )}
          <div className="flex justify-between gap-9 border border-gray-200 rounded-lg">
            <div
              className="px-6 py-2 text-black rounded-lg font-medium transition-all duration-200"
            >
              Bill
            </div>

            <button
              onClick={handleView}
              className="px-6 py-2 text-cyan-400 font-medium hover:text-cyan-500 hover:bg-cyan-50 rounded-lg transition-all duration-200"
            >
              View
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
